import { z } from "zod";
import { IdempotencyKeySchema, IdSchema, IsoDateTimeSchema, Sha256Schema } from "./primitives.js";
export const MedicationJurisdictionSchema = z.enum(["CA", "US"]);
export const MedicationAuthoritySchema = z.enum([
    "FDB_MEDKNOWLEDGE_CANADA",
    "HEALTH_CANADA_DPD",
    "HEALTH_CANADA_NNHPD",
    "HEALTH_CANADA_CCDD",
    "GS1",
    "RXNORM",
    "DAILYMED",
]);
export const MedicationLocaleSchema = z.enum(["en-CA", "fr-CA", "en-US"]);
export const FdbCanadaModuleSchema = z.enum([
    "packaged_product",
    "drug_allergy",
    "drug_drug_interaction",
    "drug_food_interaction",
    "duplicate_therapy",
    "min_max_dosing",
    "drug_disease_contraindication",
    "pregnancy",
    "pediatric",
    "geriatric",
    "drug_lab_interference",
    "canadian_clinical_drug_interoperability",
    "medknowledge_framework_canada",
    "orderknowledge_canada_data",
]);
export const FdbCanadaIdentifierTypeSchema = z.enum([
    "FDB_PRODUCT_ID",
    "DIN",
    "NPN",
    "GTIN",
    "IDC",
    "CCDD_MPID",
    "CCDD_NTPID",
]);
const IdentifierAuthorityByType = {
    FDB_PRODUCT_ID: "FDB_MEDKNOWLEDGE_CANADA",
    DIN: "HEALTH_CANADA_DPD",
    NPN: "HEALTH_CANADA_NNHPD",
    GTIN: "GS1",
    IDC: "FDB_MEDKNOWLEDGE_CANADA",
    CCDD_MPID: "HEALTH_CANADA_CCDD",
    CCDD_NTPID: "HEALTH_CANADA_CCDD",
};
function identifierFormatIsValid(type, value) {
    switch (type) {
        case "DIN":
        case "NPN":
            return /^\d{8}$/.test(value);
        case "GTIN":
            return /^(?:\d{8}|\d{12}|\d{13}|\d{14})$/.test(value);
        case "FDB_PRODUCT_ID":
        case "IDC":
        case "CCDD_MPID":
        case "CCDD_NTPID":
            return /^[A-Za-z0-9][A-Za-z0-9._:-]{0,79}$/.test(value);
    }
}
export const FdbCanadaMedicationIdentifierSchema = z
    .object({
    type: FdbCanadaIdentifierTypeSchema,
    value: z.string().min(1).max(80),
    authority: MedicationAuthoritySchema,
    jurisdiction: z.literal("CA"),
    sourceReleaseId: IdSchema,
    effectiveAt: IsoDateTimeSchema,
    retiredAt: IsoDateTimeSchema.optional(),
    provenanceHash: Sha256Schema,
})
    .strict()
    .superRefine((identifier, context) => {
    if (identifier.authority !== IdentifierAuthorityByType[identifier.type]) {
        context.addIssue({
            code: "custom",
            path: ["authority"],
            message: `${identifier.type} must use its exact governing authority`,
        });
    }
    if (!identifierFormatIsValid(identifier.type, identifier.value)) {
        context.addIssue({
            code: "custom",
            path: ["value"],
            message: `${identifier.type} has an invalid format`,
        });
    }
    if (identifier.retiredAt !== undefined &&
        Date.parse(identifier.retiredAt) < Date.parse(identifier.effectiveAt)) {
        context.addIssue({
            code: "custom",
            path: ["retiredAt"],
            message: "Identifier retirement cannot precede its effective time",
        });
    }
});
export const FdbCanadaLocalizedDisplaySchema = z
    .object({
    locale: MedicationLocaleSchema,
    display: z.string().min(1).max(500),
})
    .strict();
export const FdbCanadaProductIdentitySchema = z
    .object({
    fdbProductId: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,79}$/),
    sourceReleaseId: IdSchema,
    displays: z.array(FdbCanadaLocalizedDisplaySchema).min(1).max(8),
    identifiers: z.array(FdbCanadaMedicationIdentifierSchema).min(1).max(32),
    active: z.boolean(),
    contentHash: Sha256Schema,
})
    .strict()
    .superRefine((identity, context) => {
    const primaryIdentifiers = identity.identifiers.filter((identifier) => identifier.type === "FDB_PRODUCT_ID" && identifier.value === identity.fdbProductId);
    if (primaryIdentifiers.length !== 1) {
        context.addIssue({
            code: "custom",
            path: ["identifiers"],
            message: "A product requires exactly one matching FDB_PRODUCT_ID identifier",
        });
    }
    if (identity.identifiers.some((identifier) => identifier.sourceReleaseId !== identity.sourceReleaseId)) {
        context.addIssue({
            code: "custom",
            path: ["identifiers"],
            message: "Every identifier must bind the same exact FDB Canada release",
        });
    }
    const identifierKeys = new Set();
    for (const [index, identifier] of identity.identifiers.entries()) {
        const key = `${identifier.type}\u0000${identifier.value}`;
        if (identifierKeys.has(key)) {
            context.addIssue({
                code: "custom",
                path: ["identifiers", index],
                message: "A product cannot repeat the same typed identifier",
            });
        }
        identifierKeys.add(key);
    }
    const locales = new Set();
    for (const [index, display] of identity.displays.entries()) {
        if (display.locale === "en-US") {
            context.addIssue({
                code: "custom",
                path: ["displays", index, "locale"],
                message: "FDB Canada identity displays must use a Canadian locale",
            });
        }
        if (locales.has(display.locale)) {
            context.addIssue({
                code: "custom",
                path: ["displays", index],
                message: "A product cannot repeat a localized display",
            });
        }
        locales.add(display.locale);
    }
});
export const FdbCanadaArtifactRoleSchema = z.enum([
    "data",
    "ddl",
    "data_dictionary",
    "record_counts",
    "product_info",
    "module_manifest",
    "release_notes",
]);
export const FdbCanadaReleaseArtifactSchema = z
    .object({
    artifactId: IdSchema,
    authority: z.literal("FDB_MEDKNOWLEDGE_CANADA"),
    artifactRole: FdbCanadaArtifactRoleSchema,
    artifactName: z.string().min(1).max(240),
    schemaVersion: z.string().min(1).max(80),
    contentHash: Sha256Schema,
    byteLength: z.number().int().positive(),
    recordCount: z.number().int().nonnegative().optional(),
})
    .strict();
export const FdbCanadaReleaseManifestSchema = z
    .object({
    releaseId: IdSchema,
    tenantId: IdSchema,
    siteId: IdSchema,
    jurisdiction: z.literal("CA"),
    authority: z.literal("FDB_MEDKNOWLEDGE_CANADA"),
    releaseIdentifier: z.string().min(1).max(160),
    releaseType: z.enum(["full", "delta"]),
    productDate: z.string().regex(/^\d{8}$/),
    schemaVersion: z.string().min(1).max(80),
    predecessorReleaseId: IdSchema.optional(),
    predecessorSnapshotHash: Sha256Schema.optional(),
    fullReloadRequired: z.boolean(),
    contentStatus: z.literal("evaluation_only"),
    productionEligible: z.literal(false),
    promotionAllowed: z.literal(false),
    contentLocales: z
        .array(z.enum(["en-CA", "fr-CA"]))
        .min(1)
        .max(2),
    pilotLocales: z.tuple([z.literal("en-CA")]),
    modules: z.array(FdbCanadaModuleSchema).min(1).max(32),
    artifacts: z.array(FdbCanadaReleaseArtifactSchema).min(1).max(500),
    recordCounts: z
        .object({
        sourceRecords: z.number().int().nonnegative(),
        acceptedRecords: z.number().int().nonnegative(),
        rejectedRecords: z.number().int().nonnegative(),
    })
        .strict(),
    expectedChunkCount: z.number().int().positive().max(1_000_000),
    releaseContentHash: Sha256Schema,
    createdAt: IsoDateTimeSchema,
    createdBy: IdSchema,
    manifestContentHash: Sha256Schema,
})
    .strict()
    .superRefine((manifest, context) => {
    if (manifest.recordCounts.acceptedRecords + manifest.recordCounts.rejectedRecords !==
        manifest.recordCounts.sourceRecords) {
        context.addIssue({
            code: "custom",
            path: ["recordCounts"],
            message: "Accepted and rejected counts must equal the source count",
        });
    }
    const hasPredecessor = manifest.predecessorReleaseId !== undefined && manifest.predecessorSnapshotHash !== undefined;
    if ((manifest.releaseType === "delta") !== hasPredecessor) {
        context.addIssue({
            code: "custom",
            path: ["predecessorReleaseId"],
            message: "Only a delta release requires an exact predecessor release and snapshot hash",
        });
    }
    if (!manifest.contentLocales.includes("en-CA")) {
        context.addIssue({
            code: "custom",
            path: ["contentLocales"],
            message: "The Canada-first evaluation release requires English Canadian content",
        });
    }
    if (new Set(manifest.contentLocales).size !== manifest.contentLocales.length) {
        context.addIssue({
            code: "custom",
            path: ["contentLocales"],
            message: "Content locales must be unique",
        });
    }
    if (new Set(manifest.modules).size !== manifest.modules.length) {
        context.addIssue({
            code: "custom",
            path: ["modules"],
            message: "Release modules must be unique",
        });
    }
    if (new Set(manifest.artifacts.map((artifact) => artifact.artifactId)).size !==
        manifest.artifacts.length) {
        context.addIssue({
            code: "custom",
            path: ["artifacts"],
            message: "Release artifact identifiers must be unique",
        });
    }
});
export const FdbCanadaIdentityRecordSchema = z
    .object({
    operation: z.enum(["A", "C", "D"]),
    fdbProductId: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,79}$/),
    identity: FdbCanadaProductIdentitySchema.optional(),
})
    .strict()
    .superRefine((record, context) => {
    if ((record.operation === "D") === (record.identity !== undefined)) {
        context.addIssue({
            code: "custom",
            path: ["identity"],
            message: "Add/change records require identity content; delete records must omit it",
        });
    }
    if (record.identity !== undefined && record.identity.fdbProductId !== record.fdbProductId) {
        context.addIssue({
            code: "custom",
            path: ["identity", "fdbProductId"],
            message: "Identity content must match the record FDB_PRODUCT_ID",
        });
    }
});
export const FdbCanadaReleaseChunkSchema = z
    .object({
    releaseId: IdSchema,
    chunkIndex: z.number().int().nonnegative(),
    chunkCount: z.number().int().positive().max(1_000_000),
    previousChunkHash: Sha256Schema.optional(),
    records: z.array(FdbCanadaIdentityRecordSchema).min(1).max(1_000),
    recordCount: z.number().int().positive().max(1_000),
    contentHash: Sha256Schema,
})
    .strict()
    .superRefine((chunk, context) => {
    if (chunk.recordCount !== chunk.records.length) {
        context.addIssue({
            code: "custom",
            path: ["recordCount"],
            message: "Chunk record count must match its exact records",
        });
    }
    if ((chunk.chunkIndex === 0) !== (chunk.previousChunkHash === undefined)) {
        context.addIssue({
            code: "custom",
            path: ["previousChunkHash"],
            message: "Only the first chunk may omit the previous chunk hash",
        });
    }
    if (chunk.chunkIndex >= chunk.chunkCount) {
        context.addIssue({
            code: "custom",
            path: ["chunkIndex"],
            message: "Chunk index must be inside the declared chunk count",
        });
    }
});
export const FdbCanadaIdentifierAmbiguitySchema = z
    .object({
    type: FdbCanadaIdentifierTypeSchema,
    value: z.string().min(1).max(80),
    fdbProductIds: z.array(z.string().min(1).max(80)).min(2),
})
    .strict();
export const FdbCanadaStagedSnapshotSchema = z
    .object({
    releaseId: IdSchema,
    predecessorReleaseId: IdSchema.optional(),
    status: z.literal("staged"),
    contentStatus: z.literal("evaluation_only"),
    productionEligible: z.literal(false),
    promotionAllowed: z.literal(false),
    products: z.array(FdbCanadaProductIdentitySchema),
    ambiguities: z.array(FdbCanadaIdentifierAmbiguitySchema),
    sourceRecordCount: z.number().int().nonnegative(),
    acceptedRecordCount: z.number().int().nonnegative(),
    rejectedRecordCount: z.number().int().nonnegative(),
    snapshotHash: Sha256Schema,
})
    .strict();
export const FdbCanadaSafetyCategorySchema = z.enum([
    "allergy",
    "interaction",
    "duplicate_therapy",
    "dose",
    "route",
    "renal",
    "hepatic",
    "pregnancy",
    "age",
    "monitoring",
]);
export const FdbCanadaCoverageEntrySchema = z
    .object({
    category: FdbCanadaSafetyCategorySchema,
    status: z.enum(["candidate_coverage", "partial", "gap"]),
    sourceModules: z.array(FdbCanadaModuleSchema),
    rationale: z.string().min(1).max(1_000),
})
    .strict();
export const FdbCanadaCoverageReportSchema = z
    .object({
    releaseId: IdSchema,
    jurisdiction: z.literal("CA"),
    entries: z.array(FdbCanadaCoverageEntrySchema).length(10),
    candidateCoverageCount: z.number().int().nonnegative(),
    partialCount: z.number().int().nonnegative(),
    gapCount: z.number().int().nonnegative(),
    clinicalScreeningEnabled: z.literal(false),
    productionEligible: z.literal(false),
    generatedAt: IsoDateTimeSchema,
    contentHash: Sha256Schema,
})
    .strict();
export const FdbCanadaReleaseVerificationSchema = z
    .object({
    manifest: FdbCanadaReleaseManifestSchema,
    snapshot: FdbCanadaStagedSnapshotSchema,
    coverageReport: FdbCanadaCoverageReportSchema,
})
    .strict();
export const FdbCanadaRollbackReceiptSchema = z
    .object({
    rolledBackReleaseId: IdSchema,
    restoredReleaseId: IdSchema,
    restoredSnapshotHash: Sha256Schema,
    status: z.literal("rolled_back"),
    productionEligible: z.literal(false),
})
    .strict();
export const FdbCanadaEvaluationMedicationSchema = z
    .object({
    identity: FdbCanadaProductIdentitySchema,
    display: FdbCanadaLocalizedDisplaySchema,
    release: z
        .object({
        releaseId: IdSchema,
        releaseIdentifier: z.string().min(1).max(160),
        productDate: z.string().regex(/^\d{8}$/),
        schemaVersion: z.string().min(1).max(80),
        snapshotHash: Sha256Schema,
    })
        .strict(),
    dataStatus: z.literal("evaluation_only"),
    clinicalUseAllowed: z.literal(false),
    prescribingAllowed: z.literal(false),
})
    .strict()
    .superRefine((medication, context) => {
    if (medication.identity.sourceReleaseId !== medication.release.releaseId ||
        !medication.identity.displays.some((display) => display.locale === medication.display.locale &&
            display.display === medication.display.display)) {
        context.addIssue({
            code: "custom",
            path: ["identity"],
            message: "Evaluation lookup identity must bind the exact release and selected display",
        });
    }
});
export const FdbCanadaEvaluationLookupResponseSchema = z
    .object({
    query: z.string().min(2).max(120),
    status: z.enum(["ready", "no_evaluation_release"]),
    medications: z.array(FdbCanadaEvaluationMedicationSchema).max(50),
    release: z
        .object({
        releaseId: IdSchema,
        releaseIdentifier: z.string().min(1).max(160),
        productDate: z.string().regex(/^\d{8}$/),
        schemaVersion: z.string().min(1).max(80),
        snapshotHash: Sha256Schema,
    })
        .strict()
        .optional(),
    dataStatus: z.literal("evaluation_only"),
    clinicalScreeningStatus: z.literal("not_performed"),
    recommendationsStatus: z.literal("synthetic_demo_only"),
    productionEligible: z.literal(false),
})
    .strict()
    .superRefine((result, context) => {
    if ((result.status === "ready") !== (result.release !== undefined) ||
        (result.status === "no_evaluation_release" && result.medications.length > 0)) {
        context.addIssue({
            code: "custom",
            path: ["status"],
            message: "Lookup availability must match its staged evaluation release and results",
        });
    }
});
export const FdbCanadaDemoOrderSchema = z
    .object({
    dose: z.string().min(1).max(120),
    route: z.string().min(1).max(120),
    frequency: z.string().min(1).max(160),
    duration: z.string().min(1).max(120),
    quantity: z.number().positive().max(100_000),
    quantityUnit: z.string().min(1).max(80),
    refills: z.number().int().min(0).max(99),
    syntheticInstructions: z.string().min(1).max(1_000),
})
    .strict();
export const FdbCanadaDemoWorkflowStateSchema = z.enum([
    "draft",
    "review_ready",
    "demo_approved",
    "discarded",
]);
export const FdbCanadaDemoEventTypeSchema = z.enum([
    "draft_created",
    "draft_updated",
    "review_prepared",
    "returned_to_draft",
    "demo_approved",
    "discarded",
]);
export const FdbCanadaDemoWorkflowEventSchema = z
    .object({
    eventId: IdSchema,
    workflowId: IdSchema,
    workflowVersion: z.number().int().positive(),
    eventType: FdbCanadaDemoEventTypeSchema,
    priorState: FdbCanadaDemoWorkflowStateSchema.optional(),
    resultingState: FdbCanadaDemoWorkflowStateSchema,
    actorId: IdSchema,
    actorRole: z.enum(["physician", "nurse_ma"]),
    actorAal: z.literal("aal2"),
    occurredAt: IsoDateTimeSchema,
    contentHash: Sha256Schema,
    eventHash: Sha256Schema,
})
    .strict();
export const FdbCanadaDemoApprovalSchema = z
    .object({
    approvedBy: IdSchema,
    approvedAt: IsoDateTimeSchema,
    approvedContentHash: Sha256Schema,
    acknowledgement: z.literal("I approve this synthetic demonstration state only. No prescription is created."),
})
    .strict();
export const FdbCanadaDemoWorkflowSchema = z
    .object({
    workflowId: IdSchema,
    tenantId: IdSchema,
    siteId: IdSchema,
    version: z.number().int().positive(),
    state: FdbCanadaDemoWorkflowStateSchema,
    subjectLabel: z.literal("Synthetic demo subject"),
    medication: FdbCanadaEvaluationMedicationSchema,
    order: FdbCanadaDemoOrderSchema,
    contentHash: Sha256Schema,
    reviewedContentHash: Sha256Schema.optional(),
    demoApproval: FdbCanadaDemoApprovalSchema.optional(),
    events: z.array(FdbCanadaDemoWorkflowEventSchema).min(1),
    clinicalScreeningStatus: z.literal("not_performed"),
    recommendationsStatus: z.literal("synthetic_demo_only"),
    realPrescriptionCreated: z.literal(false),
    executionAllowed: z.literal(false),
    printable: z.literal(false),
    transmissible: z.literal(false),
    createdBy: IdSchema,
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
})
    .strict()
    .superRefine((workflow, context) => {
    if (workflow.medication.release.releaseId !== workflow.medication.identity.sourceReleaseId ||
        workflow.events.at(-1)?.workflowVersion !== workflow.version ||
        workflow.events.at(-1)?.resultingState !== workflow.state) {
        context.addIssue({
            code: "custom",
            path: ["events"],
            message: "Demo workflow must remain bound to its exact identity, version, and audit state",
        });
    }
    if ((workflow.state === "review_ready" || workflow.state === "demo_approved") &&
        workflow.reviewedContentHash !== workflow.contentHash) {
        context.addIssue({
            code: "custom",
            path: ["reviewedContentHash"],
            message: "Review-ready demo state must bind the current exact content hash",
        });
    }
    if ((workflow.state === "demo_approved") !== (workflow.demoApproval !== undefined)) {
        context.addIssue({
            code: "custom",
            path: ["demoApproval"],
            message: "Only the demo-approved state may contain a mock approval acknowledgement",
        });
    }
    if (workflow.demoApproval !== undefined &&
        workflow.demoApproval.approvedContentHash !== workflow.contentHash) {
        context.addIssue({
            code: "custom",
            path: ["demoApproval", "approvedContentHash"],
            message: "Mock approval must bind the current exact demo content",
        });
    }
});
export const CreateFdbCanadaDemoWorkflowRequestSchema = z
    .object({
    medication: FdbCanadaEvaluationMedicationSchema,
    order: FdbCanadaDemoOrderSchema,
    idempotencyKey: IdempotencyKeySchema,
})
    .strict();
export const UpdateFdbCanadaDemoWorkflowRequestSchema = z
    .object({
    expectedVersion: z.number().int().positive(),
    order: FdbCanadaDemoOrderSchema,
    idempotencyKey: IdempotencyKeySchema,
})
    .strict();
export const TransitionFdbCanadaDemoWorkflowRequestSchema = z
    .object({
    expectedVersion: z.number().int().positive(),
    transition: z.enum(["prepare_review", "return_to_draft", "approve_demo", "discard"]),
    acknowledgement: z
        .literal("I approve this synthetic demonstration state only. No prescription is created.")
        .optional(),
    idempotencyKey: IdempotencyKeySchema,
})
    .strict()
    .superRefine((request, context) => {
    if ((request.transition === "approve_demo") !== (request.acknowledgement !== undefined)) {
        context.addIssue({
            code: "custom",
            path: ["acknowledgement"],
            message: "Only demo approval requires the exact non-clinical acknowledgement",
        });
    }
});
//# sourceMappingURL=fdb-canada.js.map